import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import FormController from '../../common/formController';
import { isArray, isObject } from 'lodash';
import { actions } from '../../store/enrichedSchema';
import Joi from 'joi';
import validators from './validators';
import { getEnvVar } from 'helpers';

const {
  getEnrichedSchema,
  getEnrichedSchemaKeys,
  createEnrichedSchema,
  updateEnrichedSchema,
  setCurrentEnrichedSchema,
  setCurrentEnrichedSchemaDetails,
  loadingCurrentEnrichedSchema,
  creatingEnrichedSchemaFailed
} = actions;

let formController = new FormController();

export default function useEnrichedSchema(nextPath, backPath) {
  const dispatch = useDispatch();
  const history = useHistory();

  const { enrichedSchemaId } = useParams();

  const schema = useSelector(store => store.enrichedSchema.current);
  const schemaDetails = useSelector(
    store => store.enrichedSchema.currentDetails
  );
  const schemaKeys = useSelector(store => store.enrichedSchema.keys);
  const isLoading = useSelector(store => store.enrichedSchema.getting);
  const schemaSaveFailed = useSelector(
    store => store.enrichedSchema.schemaSaveFailed
  );
  const currentTenantId = useSelector(
    store => store.user.selectedTenant.tenantId
  );
  const currentBusinessContextId = useSelector(
    store => store.user.selectedBusinessContext.businessContextId
  );

  const [schemaObject, setSchemaObject] = useState({});
  const [schemaObjectCopy, setSchemaObjectCopy] = useState({});
  const [isInvalidPayload, setIsInvalidPayload] = useState(false);
  const [errorValidationSegment, setErrorValidationSegment] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [enrichmentProducts, setEnrichmentProducts] = useState([]);

  const disabledInputs = ['tenantId', 'businessContextId', 'enrichmentProduct'];
  const requiredFields = [...disabledInputs, 'schemaName'];
  const MASKED_KEYS = ['uuid', 'createTime', 'updateTime'];

  useEffect(() => {
    let products =
      (getEnvVar('ADM_ENRICHMENT_PRODUCTS') || '').split(',') || [];
    setEnrichmentProducts(products);
    if (enrichedSchemaId) {
      dispatch(getEnrichedSchema({ force: true, schemaId: enrichedSchemaId }));
      formController = new FormController();
    } else {
      if (Object.keys(schema).length > 0) {
        formController = new FormController();
        setSchemaObject(schemaObject =>
          createObjectFromValidSchema({ ...schemaObject, ...schema })
        );
      }
    }
  }, []);

  useEffect(() => {
    if (schemaSaveFailed) {
      setSchemaObject(schemaObject =>
        createObjectFromValidSchema({ ...schemaObject, ...schema })
      );
      dispatch(loadingCurrentEnrichedSchema());
    }
  }, [schemaSaveFailed]);

  useEffect(() => {
    if (schemaKeys) {
      let schemaObj = createObjectFromSchema(schemaKeys, '') || {};
      setSchemaObject(schemaObject =>
        createObjectFromValidSchema({ ...schemaObj, ...(JSON.parse(JSON.stringify(schemaObject))) })
      );
      let schemaObjCopy = createObjectFromSchema(schemaKeys, '', true) || {};
      setSchemaObjectCopy(JSON.parse(JSON.stringify(schemaObjCopy)));
    }
  }, [schemaKeys]);

  useEffect(() => {
    if (schema) {
      setSchemaObject(schemaObject =>
        createObjectFromValidSchema({ ...schemaObject, ...schema })
      );
    }
  }, [schema]);

  const navBackOnError = path => {
    if ((!schemaDetails.name || !schemaDetails.option) && !schema.uuid) {
      history.push(path);
    }
  };

  const updateCurrentErrorValidationSegment = name => {
    setErrorValidationSegment(name);
  };

  const validate = (saveAttempted = false) => {
    let segment = errorValidationSegment;
    const joiValidators = Joi.object(validators[segment]);
    const validation = joiValidators.validate(
      segment === 'details' ? schemaDetails : schemaObject,
      {
        abortEarly: false,
        allowUnknown: true
      }
    );
    let errors = formController.getFormErrors(validation, { saveAttempted });
    setValidationErrors(errors);
    return errors;
  };

  const getCurrentSchemaKeys = () => {
    dispatch(getEnrichedSchemaKeys());
  };

  const createStringFromSchema = (objectJson, key) => {
    switch (key) {
      case 'businessContextId':
        objectJson[key] = currentBusinessContextId;
        break;
      case 'tenantId':
        objectJson[key] = currentTenantId;
        break;
      case 'schemaName':
        objectJson[key] = schemaDetails.name;
        break;
      case 'enrichmentProduct':
        objectJson[key] = schemaDetails.option || '';
        break;
      case 'uuid':
        break;
      default:
        objectJson[key] = '';
        break;
    }
    return objectJson;
  };

  function CreateArrayobjectFromSchema(value, objectJson, parentKeyPath, key, includeArrayElements) {
    switch (value.items.type) {
      case 'object': objectJson = [
        createObjectFromSchema(
          value.items.properties,
          parentKeyPath + key + '.',
          includeArrayElements
        )
      ];
        break;
      case 'string': objectJson = [''];
        break;
    }
    return objectJson;
  }

  const createObjectFromSchema = (object, parentKeyPath, includeArrayElements) => {
    let objectJson = {};
    let keys = parentKeyPath.split('.');
    Object.entries(object).forEach(entry => {
      let key = entry[0];
      let value = entry[1];
      if (value.type === 'string') {
        objectJson = createStringFromSchema(objectJson, key);
      } else if (value.type === 'boolean') {
        if (
          (keys.indexOf('eventListFields') >= 0 && key === 'campaignVisible') ||
          key === 'templateVisible'
        ) {
          objectJson[key] = true;
        } else {
          objectJson[key] = false;
        }
      } else if (value.type === 'array') {
        objectJson[key] = includeArrayElements ? CreateArrayobjectFromSchema(value, objectJson[key], parentKeyPath, key, includeArrayElements) : [];
      }
    });
    return objectJson;
  };

  const createObjectFromValidSchema = object => {
    let objectJson = {};
    Object.entries(object).forEach(entry => {
      let key = entry[0];
      let value = entry[1];
      if (isArray(value)) {
        objectJson[key] = [];
        value.map(obj => {
          objectJson[key].push(
            isObject(obj) ? createObjectFromValidSchema(obj) : obj
          );
        });
      } else if (key === 'schemaName') {
        objectJson[key] = object.schemaName;
      } else if (MASKED_KEYS.indexOf(key) < 0) {
        objectJson[key] = value;
      }
    });
    return objectJson;
  };

  const shouldShowError = name => {
    return formController.shouldShowError(validationErrors, name);
  };

  const getErrorMessage = name => {
    return formController.getErrorMessage(validationErrors, name);
  };

  const validateJsonPayload = () => {
    try {
      schemaObject['examplePayload'] !== '' &&
        JSON.parse(schemaObject['examplePayload']);
      setIsInvalidPayload(false);
      return true;
    } catch (ex) {
      setIsInvalidPayload(true);
      return false;
    }
  };

  const handleChange = event => {
    dispatch(
      setCurrentEnrichedSchemaDetails({
        ...schemaDetails,
        name: event.target.value
      })
    );
    validate();
  };

  const handleOptionsChange = event => {
    dispatch(
      setCurrentEnrichedSchemaDetails({
        ...schemaDetails,
        option: event.target.value
      })
    );
    validate();
  };

  const handleObjectChange = object => {
    setSchemaObject({ ...object });
  };

  const handleBlur = event => {
    formController.onBlur(event);
    validate();
  };

  const onFocus = e => {
    formController.onFocus(e);
  };

  const goNext = event => {
    let errors = validate(true);
    if (errors.meta.clean) {
      dispatch(
        setCurrentEnrichedSchemaDetails({
          ...schemaDetails
        })
      );
      dispatch(
        setCurrentEnrichedSchema({
          ...schema,
          schemaName: schemaDetails.name,
          enrichmentProduct: schemaDetails.option
        })
      );
      history.push(
        `${nextPath || event.currentTarget.attributes.link.value}${
          enrichedSchemaId ? `/${enrichedSchemaId}` : ''
        }`
      );
    }
  };

  const goBack = event => {
    dispatch(setCurrentEnrichedSchema({ ...schemaObject }));
    dispatch(
      setCurrentEnrichedSchemaDetails({
        ...schemaDetails,
        hasMovedToNextStep: true,
        name: schemaObject.schemaName
      })
    );
    history.push(
      `${backPath || event.currentTarget.attributes.link.value}${
        enrichedSchemaId ? `/${enrichedSchemaId}` : ''
      }`
    );
  };

  const saveSchema = () => {
    let errors = validate(true);
    let isValidJson = validateJsonPayload();
    let isValidSchema = errors.meta.clean && isValidJson;
    if (enrichedSchemaId) {
      dispatch(setCurrentEnrichedSchema({ ...schemaObject }));
      if (isValidSchema) {
        dispatch(
          updateEnrichedSchema({
            schema: schemaObject,
            schemaId: enrichedSchemaId
          })
        );
      }
    } else {
      let currentSchema = {
        ...schemaObject,
        examplePayload: schemaObject['examplePayload']
      };
      dispatch(setCurrentEnrichedSchema(currentSchema));
      if (isValidSchema) {
        dispatch(createEnrichedSchema({ schema: currentSchema }));
      }
    }
    if (!isValidSchema) {
      dispatch(creatingEnrichedSchemaFailed({ isValidationFailed: true }));
    }
  };

  const closeSchema = () => {
    formController = new FormController();
    history.push('/enriched-schema');
  };

  return {
    isLoading,
    schemaKeys,
    enrichedSchemaId,
    schemaObject,
    schemaObjectCopy,
    validationErrors,
    isInvalidPayload,
    enrichmentProducts,
    disabledInputs,
    requiredFields,
    schemaSaveFailed,
    getCurrentSchemaKeys,
    navBackOnError,
    handleChange,
    handleOptionsChange,
    handleObjectChange,
    updateCurrentErrorValidationSegment,
    validate,
    validateJsonPayload,
    shouldShowError,
    getErrorMessage,
    handleBlur,
    onFocus,
    goNext,
    goBack,
    saveSchema,
    closeSchema
  };
}
